<template>
  <div v-if="!loading">
    <CCard>
      <CCardBody>
        <div>
          <h4 class="card-title mb-0">Representative Restaurant Report</h4>
          <div class="small text-muted">Track restaurant bindings by representatives</div>
        </div>

        <!-- Filters -->
        <CCard class="filters my-3" accent-color="warning">
          <CCardBody class="p-2">
            <div class="grid">
              <v-select
                class="v-select-filter"
                v-model="filters.period"
                :options="periodOptions"
                :searchable="false"
                placeholder="Select period..."
              />

              <CInput
                v-if="filters.period && filters.period.value === 'custom'"
                type="date"
                label="Start:"
                v-model="filters.startDate"
                class="d-flex flex-gap-2 align-items-center mb-0 date-input"
              />

              <CInput
                v-if="filters.period && filters.period.value === 'custom'"
                type="date"
                label="End:"
                v-model="filters.endDate"
                class="d-flex flex-gap-2 align-items-center mb-0 date-input"
              />
            </div>
          </CCardBody>
        </CCard>

        <!-- Stats Cards -->
        <div class="row mb-4 stats-cards">
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown
              color="primary"
              :header="`${reportData.stats.totalRepresentatives}`"
              text="Total Representatives"
            />
          </div>
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown
              color="info"
              :header="`${reportData.stats.totalRestaurants}`"
              text="Total Restaurants"
            />
          </div>
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown
              color="success"
              :header="`${reportData.stats.totalRepresentativeAdded || 0}`"
              text="Rep. Added"
            />
          </div>
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown
              color="warning"
              :header="`${reportData.stats.totalMenuteReferred || 0}`"
              text="Menute Referred"
            />
          </div>
        </div>

        <!-- Representatives Table -->
        <div class="table-responsive">
          <CDataTable
            striped
            hover
            :items="reportData.data"
            :fields="[
              { key: 'representativeName', label: 'Representative Name', _style: 'min-width:200px' },
              { key: 'representativeEmail', label: 'Email', _style: 'min-width:250px' },
              { key: 'representativeAddedCount', label: 'Rep. Added', _style: 'width:120px; text-align: center;' },
              { key: 'menuteReferredCount', label: 'Menute Referred', _style: 'width:140px; text-align: center;' },
              { key: 'totalCount', label: 'Total', _style: 'width:100px; text-align: center;' },
              { key: 'firstRestaurantDate', label: 'First Restaurant', _style: 'min-width:150px; text-align: center;' },
              { key: 'lastRestaurantDate', label: 'Last Restaurant', _style: 'min-width:150px; text-align: center;' },
            ]"
            :loading="loading"
            :noItemsView="{
              noResults: 'No filtering results are available!',
              noItems: 'No representatives found!',
            }"
          >
            <template #representativeAddedCount="{item}">
              <td class="text-center">
                <CBadge color="success" shape="pill">
                  {{ item.representativeAddedCount }}
                </CBadge>
              </td>
            </template>

            <template #menuteReferredCount="{item}">
              <td class="text-center">
                <CBadge color="warning" shape="pill">
                  {{ item.menuteReferredCount }}
                </CBadge>
              </td>
            </template>

            <template #totalCount="{item}">
              <td class="text-center">
                <CBadge color="info" shape="pill">
                  {{ item.totalCount }}
                </CBadge>
              </td>
            </template>

            <template #firstRestaurantDate="{item}">
              <td class="text-center">
                {{ formatDate(item.firstRestaurantDate) }}
              </td>
            </template>

            <template #lastRestaurantDate="{item}">
              <td class="text-center">
                {{ formatDate(item.lastRestaurantDate) }}
              </td>
            </template>
          </CDataTable>
        </div>
      </CCardBody>
    </CCard>
  </div>
  <CSpinner v-else color="primary" />
</template>

<script>
import _debounce from 'lodash/debounce';

export default {
  name: 'RepresentativeRestaurantReport',
  data() {
    return {
      loading: true,
      periodOptions: [
        { value: 'week', label: 'This Week' },
        { value: 'last_week', label: 'Last Week' },
        { value: 'month', label: 'This Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'this_year', label: 'This Year' },
        { value: 'last_year', label: 'Last Year' },
        { value: 'custom', label: 'Custom Date Range' },
      ],
      filters: {
        period: { value: 'month', label: 'This Month' },
        startDate: null,
        endDate: null,
      },
      reportData: {
        data: [],
        stats: {
          totalRepresentatives: 0,
          totalRestaurants: 0,
          totalRepresentativeAdded: 0,
          totalMenuteReferred: 0,
          averageRestaurantsPerRepresentative: 0,
          maxRestaurants: 0,
          minRestaurants: 0,
        },
        filters: {},
      },
    }
  },

  watch: {
    'filters.period': {
      handler() {
        if (this.filters.period && this.filters.period.value !== 'custom') {
          this.filters.startDate = null;
          this.filters.endDate = null;
          this.fetchReport();
        }
        // If custom is selected, don't fetch yet - wait for dates
      }
    },
    'filters.startDate': {
      handler() {
        if (this.filters.period && this.filters.period.value === 'custom') {
          // Only fetch if both dates are selected
          if (this.filters.startDate && this.filters.endDate) {
            this.fetchReportByDebounce();
          }
        }
      }
    },
    'filters.endDate': {
      handler() {
        if (this.filters.period && this.filters.period.value === 'custom') {
          // Only fetch if both dates are selected
          if (this.filters.startDate && this.filters.endDate) {
            this.fetchReportByDebounce();
          }
        }
      }
    },
  },

  created() {
    this.fetchReport();
  },

  beforeMount() {
    this.fetchReportByDebounce = _debounce(() => {
      this.fetchReport();
    }, 1000);
  },

  methods: {
    async fetchReport() {
      // Don't fetch if custom period is selected but dates are not complete
      if (this.filters.period?.value === 'custom') {
        if (!this.filters.startDate || !this.filters.endDate) {
          return;
        }
      }

      this.loading = true;
      
      var url = new URL(this.$backend.REPORTS.REPRESENTATIVE.RESTAURANT_REPORT),
        params = {
          period: this.filters.period?.value || 'month',
        };

      if (this.filters.period?.value === 'custom') {
        params.start_date = this.filters.startDate;
        params.end_date = this.filters.endDate;
      }

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      try {
        const { data } = await this.$axios.get(url)
        this.reportData = data
        this.loading = false
      } catch (error) {
        this.$toast.error('Failed to fetch representative restaurant report')
        this.loading = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
  }
}
</script>

<style>
.filters .grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.stats-cards .card .card-body {
  padding-bottom: 20px !important;
}

.table th, .table td {
  vertical-align: middle;
}

.date-input input {
  font-size: 1rem;
  font-weight: bold;
  color: #f9b115 !important;
}
.date-input label {
  margin-bottom: 0 !important;
  min-width: 40px;
}
</style>

